import { escapeHtml } from "@/lib/rich-text";

interface GuidanceTemplateVariable {
  key: string;
  label: string;
  description: string;
}

export const GUIDANCE_TEMPLATE_VARIABLES: GuidanceTemplateVariable[] = [
  { key: "project_name", label: "Project", description: "The project's name" },
  { key: "demand_name", label: "Demand", description: "The project's demand name" },
  {
    key: "auditor_name",
    label: "Auditor",
    description: "The name of the auditor selected in the pop-up",
  },
  {
    key: "guidance",
    label: "Guidance",
    description: "The number of hours entered in the pop-up's Guidance field",
  },
  {
    key: "month",
    label: "Month",
    description: 'The current month, automatically filled in (e.g. "September")',
  },
];

export const DEFAULT_GUIDANCE_REQUEST_TEMPLATE = `Requesting guidance on {{project_name}} ({{demand_name}}).

Auditor: {{auditor_name}}
Estimated hours: {{guidance}}

Please advise on next steps.`;

interface GuidanceTemplateValues {
  project_name?: string;
  demand_name?: string;
  auditor_name?: string;
  guidance?: string;
  month?: string;
}

// The rich editor can leave markup or &nbsp; around a token ({{<strong>guidance</strong>}},
// {{&nbsp;guidance}}), so tolerate both inside the braces.
const PADDING = String.raw`(?:\s|&nbsp;|<\/?[a-z][^>]*>)*`;
const TOKEN = new RegExp(String.raw`\{\{${PADDING}(\w+)${PADDING}\}\}`, "gi");

/**
 * Replaces {{variable_name}} tokens with the matching value. A token with no
 * (or blank) value is left untouched so the user can see which placeholders
 * still need to be filled in as they make selections. Pass `blankUnresolved`
 * when the result is final (e.g. an outgoing email) to drop them instead, and
 * `escapeHtml` when the template is HTML so values can't inject markup.
 */
export function renderGuidanceTemplate(
  template: string,
  values: GuidanceTemplateValues,
  options?: { blankUnresolved?: boolean; escapeHtml?: boolean },
): string {
  const withAutoFilled: GuidanceTemplateValues = {
    month: new Date().toLocaleString("en-US", { month: "long" }),
    ...values,
  };
  return template.replace(TOKEN, (match, key: string) => {
    const value = withAutoFilled[key as keyof GuidanceTemplateValues];
    if (value && value.trim() !== "") return options?.escapeHtml ? escapeHtml(value) : value;
    return options?.blankUnresolved ? "" : match;
  });
}
